import type { createUserApiClient, UserInfo } from './userApiClient'

interface Dependencies {
  apiClient: Pick<ReturnType<typeof createUserApiClient>, 'getUserInfo'>
}

export class UserAuthenticationClientError extends Error {
  readonly cause: unknown

  constructor(cause: unknown) {
    super('Failed to retrieve user authentication information')
    this.name = 'UserAuthenticationClientError'
    this.cause = cause
  }
}

export function createUserAuthenticationClient({ apiClient }: Dependencies) {
  let cachedUserInfo: UserInfo | undefined
  let pendingRequest: Promise<UserInfo> | undefined
  let cacheGeneration = 0
  let latestRequestId = 0

  function getUserInfo(): Promise<UserInfo> {
    if (cachedUserInfo)
      return Promise.resolve(cachedUserInfo)

    if (pendingRequest)
      return pendingRequest

    return retrieveUserInfo()
  }

  function refreshUserInfo(): Promise<UserInfo> {
    return retrieveUserInfo()
  }

  function invalidateUserInfo() {
    cachedUserInfo = undefined
    pendingRequest = undefined
    cacheGeneration++
    latestRequestId++
  }

  function retrieveUserInfo() {
    const requestGeneration = cacheGeneration
    const requestId = ++latestRequestId
    const request = Promise.resolve().then(() => apiClient.getUserInfo())

    const pending = request
      .then((userInfo) => {
        if (requestGeneration === cacheGeneration && requestId === latestRequestId)
          cachedUserInfo = userInfo

        return userInfo
      })
      .catch((error: unknown) => {
        if (error instanceof UserAuthenticationClientError)
          throw error

        throw new UserAuthenticationClientError(error)
      })
      .finally(() => {
        if (pendingRequest === pending)
          pendingRequest = undefined
      })

    pendingRequest = pending

    return pending
  }

  return {
    getUserInfo,
    refreshUserInfo,
    invalidateUserInfo,
  }
}
